import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type RefObject,
} from "react";
import { queryPlaces as defaultQueryPlaces } from "./placesDirectory";
import type { PlaceSuggestion } from "../types";

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 3;

export interface UsePlaceAutocompleteOptions {
  /** Current address text held by the draft. */
  value: string;
  /** Called on every keystroke — the address is free text until one is chosen. */
  onChange: (address: string) => void;
  /** Called when a suggestion is chosen, separate from a plain edit. */
  onSelect?: (place: PlaceSuggestion) => void;
  /** Injectable lookup; defaults to the server-proxied geocoder. */
  search?: (query: string) => Promise<PlaceSuggestion[]>;
}

export interface UsePlaceAutocompleteResult {
  /** Attach to the wrapper the dropdown lives in; drives outside-click close. */
  rootRef: RefObject<HTMLDivElement>;
  matches: PlaceSuggestion[];
  /** Index of the highlighted row, or -1 when nothing is highlighted. */
  activeIndex: number;
  /** Lookup failure text, empty when the last query succeeded. */
  error: string;
  loading: boolean;
  /** Whether the listbox should render at all. */
  showList: boolean;
  handleInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleFocus: () => void;
  handleKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  handleHighlight: (index: number) => void;
  handleChoose: (place: PlaceSuggestion) => void;
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

/**
 * Owns the address combobox's search, highlight, and open/close state so
 * `PropertyAddressField` stays presentational.
 *
 * Suggestions are only requested once the query is long enough to be meaningful,
 * and an in-flight response is discarded when a newer keystroke supersedes it, so
 * a slow early reply cannot overwrite a later one. A lookup failure is surfaced
 * as text rather than raised: the geocoder is an aid, not a gate.
 *
 * @param {UsePlaceAutocompleteOptions} options - Draft value, change callbacks, and lookup.
 * @returns {UsePlaceAutocompleteResult} Derived list state and DOM event handlers.
 */
export function usePlaceAutocomplete({
  value,
  onChange,
  onSelect,
  search = defaultQueryPlaces,
}: UsePlaceAutocompleteOptions): UsePlaceAutocompleteResult {
  const [matches, setMatches] = useState<PlaceSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);
  // The text of the suggestion last chosen. While the box still shows it, there
  // is nothing to search for — re-querying would reopen the list over a settled
  // answer every time the field regains focus.
  const chosen = useRef("");

  // A new result set invalidates the highlight, which would otherwise point past
  // the end of a list that has since shrunk. Done here rather than in an effect
  // keyed on `matches`, so the list and the highlight never disagree for a render.
  const applyMatches = useCallback((next: PlaceSuggestion[]): void => {
    setMatches(next);
    setActiveIndex(-1);
  }, []);

  useEffect(() => {
    const query = value.trim();
    if (query.length < MIN_QUERY_LENGTH || value === chosen.current) {
      applyMatches([]);
      setLoading(false);
      return;
    }
    let live = true;
    setLoading(true);
    const handle = setTimeout(() => {
      search(query)
        .then((results) => {
          if (!live) return;
          applyMatches(results);
          setError("");
          setLoading(false);
        })
        .catch((cause: unknown) => {
          if (!live) return;
          applyMatches([]);
          // Address lookup being down must not block the form, so this reads as
          // an aside rather than a validation failure.
          setError(errorMessage(cause, "Address lookup is unavailable"));
          setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      live = false;
      clearTimeout(handle);
    };
  }, [applyMatches, value, search]);

  useEffect(() => {
    function onPointer(event: MouseEvent): void {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, []);

  const handleChoose = useCallback(
    (place: PlaceSuggestion): void => {
      chosen.current = place.label;
      onChange(place.label);
      onSelect?.(place);
      applyMatches([]);
      setOpen(false);
      setError("");
    },
    [applyMatches, onChange, onSelect],
  );

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      chosen.current = "";
      onChange(event.target.value);
      setOpen(true);
      setActiveIndex(-1);
    },
    [onChange],
  );

  const handleFocus = useCallback((): void => setOpen(true), []);

  const handleHighlight = useCallback((index: number): void => setActiveIndex(index), []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>): void => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setOpen(true);
        setActiveIndex((index) => Math.min(index + 1, matches.length - 1));
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex((index) => Math.max(index - 1, -1));
      } else if (event.key === "Enter") {
        // Results can arrive after the highlight moved, so the row may be gone.
        // With no row highlighted the key must fall through to the form, not be
        // swallowed by a dropdown that happens to be open.
        const place = open && activeIndex >= 0 ? matches[activeIndex] : undefined;
        if (!place) return;
        event.preventDefault();
        handleChoose(place);
      } else if (event.key === "Escape") {
        setOpen(false);
      }
    },
    [activeIndex, handleChoose, matches, open],
  );

  return {
    rootRef,
    matches,
    activeIndex,
    error,
    loading,
    showList: open && (matches.length > 0 || loading),
    handleInputChange,
    handleFocus,
    handleKeyDown,
    handleHighlight,
    handleChoose,
  };
}
